import { Component, OnInit, Input, OnDestroy, ViewEncapsulation, Inject, AfterViewInit, ElementRef, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { CorrelationService } from '@wdpr/ra-angular-correlation';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { Subject, fromEvent } from 'rxjs';

import { UrlParamsService } from '@finder/shared/services/url-params/url-params.service';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { SquirtService } from '@finder/shared/features/squirt/squirt-service/squirt.service';
import { CommonModule, DOCUMENT } from '@angular/common';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { DebugService } from '@finder/features/debug/services/debug-service/debug.service';
import { ScrollToService } from '@finder/shared/services/scroll-to/scroll-to.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FontIconComponent } from '../font-icon/font-icon.component';
import { ButtonComponent } from '../button/button.component';
import { JsonEditorComponent } from '../json-editor/json-editor.component';
import { LanguageSelectorComponent } from '@finder/features/debug/components/language-selector/language-selector.component';

@Component({
    selector: 'finder-debug-panel',
    templateUrl: './debug-panel.component.html',
    styleUrls: ['./debug-panel.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: true,
    imports: [
        CommonModule,
        FontIconComponent,
        ButtonComponent,
        JsonEditorComponent,
        LanguageSelectorComponent,
    ]
})
export class DebugPanelComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() svcResponse;

    pageType: string;
    entityId: string;
    entityType: string;
    location: string;
    urlFriendly: string;
    destroy$ = new Subject<void>();
    showEditor = false;
    showPanel = true;
    showModal = false;
    conversationUUID: string;
    debugLogOutput: string[] = [];

    private destroyRef = inject(DestroyRef);

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private route: ActivatedRoute,
        private urlParamsService: UrlParamsService,
        private correlationService: CorrelationService,
        private debugLog: DebugLogService,
        private squirtService: SquirtService,
        private elementReference: ElementRef,
        private windowRef: WindowRef,
        private debugService: DebugService,
        private scrollToService: ScrollToService,
    ) {
        // The subject must be subscribed within the ctor, not OnInit
        this.debugService.editorData$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.focusPanel();
            });
    }

    ngOnInit() {
        if (!this.svcResponse) {
            this.showPanel = false;

            return;
        }

        this.route.paramMap
            .pipe(
                switchMap((params: ParamMap) => {
                    this.urlParamsService.setParams(params);
                    this.entityType = this.urlParamsService.entityType;
                    this.location = this.urlParamsService.location;
                    this.urlFriendly = this.urlParamsService.id;

                    return this.route.data;
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(data => {
                this.getDebugData(data);
            });

        this.setupDebugOutput();
    }

    /* istanbul ignore next */
    getElementReference() {
        return this.elementReference.nativeElement;
    }

    /* istanbul ignore next */
    getDocument() {
        return this.document;
    }

    ngAfterViewInit(): void {
        if (!this.showPanel || !this.showModal) {
            return;
        }

        this.addModalButton();
    }

    // Ignore the if just for coverage, there's no need to test unsubscribe
    /* istanbul ignore next */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getDebugData(data = this.route.snapshot.data) {
        const svc = this.svcResponse || data.svc;
        this.pageType = this.getPageType(data.pageType);
        this.entityId = (svc || {}).id;
        this.showEditor = svc ? true : false;
        this.showModal = svc?.debug?.showModal ?? false;
        this.conversationUUID = this.correlationService.getCookieData(
            this.correlationService.getConversationCookieName()
        );
    }

    addModalButton() {
        // Create the new modal button
        const modalButton = this.getDocument().createElement('button');
        modalButton.textContent = 'Open debug modal';
        modalButton.classList.add('open-debug-modal');

        // Create a parent div for the button
        const parentDiv = this.getDocument().createElement('div');
        parentDiv.classList.add('modal-wrapper');
        parentDiv.append(modalButton);

        // Find the header and add the new element
        this.getDocument().body.append(parentDiv);

        // Listen for button click
        fromEvent(this.getDocument().body, 'click')
            .pipe(
                filter(event => (event.target as HTMLElement).classList.contains(modalButton.className)),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.openModal();
            });
    }

    openModal() {
        const FREEZE_BACKGROUND = 'freeze-background';

        // Find the dialog reference
        this.getElementReference().querySelectorAll('.debug-modal').forEach(modal => {
            // Open the modal
            modal.showModal();

            // Update the body to stay put
            this.getDocument().body.classList.add(FREEZE_BACKGROUND);

            /**
             * Listen for the modal close event
             * The modal is closed via a form button in the dialog element
             */
            fromEvent(modal, 'close')
                .pipe(
                    take(1),
                )
                .subscribe(() => {
                    // Revert the body
                    this.getDocument().body.classList.remove(FREEZE_BACKGROUND);
                });
        });
    }

    focusPanel() {
        if (this.showModal) {
            this.openModal();

            return;
        }

        // Get json editor
        const jsonEditor = this.windowRef.nativeWindow.document.querySelector('#json');
        if (!jsonEditor) {
            this.debugLog.error('***** Could not find json editor!');

            return;
        }

        // Scroll the editor into view
        this.scrollToService.scrollToElement(jsonEditor, { smooth: true });
    }

    async loadSquirt() {
        const mediaData = await this.squirtService.checkImages(this.document);
        this.squirtService.createComponent(this.document, mediaData);
    }

    // Since the detail and campaign pages get loaded by the dynamic loader
    // they always report as "dynamic" - this fixes that issue
    private getPageType(pageType) {
        if (pageType !== 'dynamic') {
            return pageType;
        }

        if (this.svcResponse.isOverview) {
            pageType = 'overview';
        } else if (this.svcResponse.isCampaign === true) {
            pageType = 'campaign';
        } else {
            pageType = 'detail';
        }

        return pageType;
    }

    /* istanbul ignore next */
    private setupDebugOutput() {
        this.debugLog.output.subscribe(output => {
            if (!output) {
                return;
            }

            this.debugLogOutput.push(
                output.map(out => {
                    if (typeof out === 'object') {
                        try {
                            out = JSON.stringify(out, null, 4);
                        } catch { }
                    }

                    return out;
                }).join(' ').replace('***** ', '') + '\n'
            );
        });
    }
}
